import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Modal, Button } from '@mui/material';
import moment from 'moment-timezone';
import Sidebar from '../../components/Sidebar/Sidebar';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { searchRefundsByDate } from '../../utils/apiUtils';
import './Refunds.scss';

const RefundTracking = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 15;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  };

  const openModal = (refund) => {
    setModalOpen(true);
    setSelectedRefund(refund);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRefund(null);
  };

  useEffect(() => {
    const fetchRefundsByDate = async () => {
      if (startDate && endDate) {
        try {
          const startInParis = moment.utc(startDate).tz('Europe/Paris').startOf('day').toISOString();
          const endInParis = moment.utc(endDate).tz('Europe/Paris').endOf('day').toISOString();

          const refundsData = await searchRefundsByDate(startInParis, endInParis);
          setRefunds(refundsData);
        } catch (error) {
          console.error('Error fetching refunds:', error);
        }
      }
    };

    fetchRefundsByDate();
  }, [startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const totalRefundsInitiated = refunds.filter(refund => refund.refundPortalSent !== null).length;
  const totalRefundsSuccessful = refunds.filter(refund => refund.refund_success === true && refund.comment_success === true && refund.cancel_success === true).length;
  const totalRefundsFailed = refunds.filter(refund => refund.refund_success === false || refund.comment_success === false || refund.cancel_success === false).length;
  const hadRefundableSubscriptionsCount = refunds.filter(refund => refund.refundableSubscriptionsCount !== 0).length;

  
  

  return (
    <Box className="StyledContainer">
      <Sidebar />

      <Box className="StyledContent">
        <Typography variant="h2">Refund Tracking</Typography>
        <Typography variant="body1">UTC calculated to Paris Timezone.</Typography>

        <Box className="StyledButtonContainer">
          <Box className="StyledCard">
            <Typography variant="h5">Date Range</Typography>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          </Box>
          <Box className="StyledCard">
            <Typography variant="h5">Overview</Typography>
            <Typography variant="body2">Total Refunds Initiated: {totalRefundsInitiated}</Typography>
            <Typography variant="body2">Total Refunds Successful: {totalRefundsSuccessful}</Typography>
            <Typography variant="body2">Total Refunds Failed: {totalRefundsFailed}</Typography>
            <Typography variant="body2">Had Refundable Subscriptions Count: {hadRefundableSubscriptionsCount}</Typography>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket ID</TableCell>
                <TableCell>Success</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Portal Triggered</TableCell>
                <TableCell>Completed At</TableCell>
                <TableCell>More Info</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? refunds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : refunds
              ).map((refund) => (
                <TableRow key={refund.refund_id}>
                  <TableCell>{refund.ticket_id}</TableCell>
                  <TableCell>
                    {refund.refund_success === true ? 'Successful' :
                    refund.refund_success === false ? 'Failed' :
                    'N/A'}
                  </TableCell>
                  <TableCell>{refund.refundAmount ? refund.refundAmount / 100 : "-"}</TableCell>
                  <TableCell>{formatDate(refund.refundPortalSent)}</TableCell>
                  <TableCell>{formatDate(refund.cancelledSubscription)}</TableCell>
                  <TableCell>
                  <Button variant="contained" onClick={() => openModal(refund)}>More Details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[15]}
            component="div"
            count={refunds.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableContainer>

        <Modal open={modalOpen} onClose={closeModal}>
  <Box className="modalContent">
    <Typography variant="h5">Refund Details</Typography>
    {selectedRefund && (
      <ul>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Success:</span> {selectedRefund.refund_success ? 'Yes' : 'No'}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Cancel Success:</span> {selectedRefund.cancel_success ? 'Yes' : 'No'}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Comment Success:</span> {selectedRefund.comment_success ? 'Yes' : 'No'}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Ticket ID:</span> {selectedRefund.ticket_id}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Customer ID:</span> {selectedRefund.customerId}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Welcome Sent:</span> {formatDate(selectedRefund.welcomeMessageSent)}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Success:</span> {selectedRefund.success ? 'Yes' : 'No'}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Amount:</span> {selectedRefund.refundAmount}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Portal Sent:</span> {formatDate(selectedRefund.refundPortalSent)}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refundable Subscription Count:</span> {selectedRefund.refundableSubscriptionsCount}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Cancelled Subscription:</span> {formatDate(selectedRefund.cancelledSubscription)}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Reason:</span> {selectedRefund.reason}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Credit Note:</span> {selectedRefund.refundResultCreditNote}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Invoice:</span> {selectedRefund.refundResultInvoice}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Transaction:</span> {selectedRefund.refundResultTransaction}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Process Refund Triggered:</span> {formatDate(selectedRefund.processRefundTriggered)}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Refund Subscription Triggered:</span> {formatDate(selectedRefund.refundSubscriptionTriggered)}
        </li>
        <li>
          <span style={{ fontWeight: 'bold' }}>Check Refund Pattern Results:</span> {selectedRefund.checkRefundPatternResults ? 'Yes' : 'No'}
        </li>
      </ul>
    )}
    <button className="closeButton" onClick={closeModal}>
      Close
    </button>
  </Box>
</Modal>

      </Box>
    </Box>
  );
};

export default RefundTracking;
